import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Printer } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { devApi, DOC_STATE_LABEL } from './devApi';
import { jstDateTime } from '@/utils/jstDate';

const fmt = (s) => (s ? jstDateTime(s) : '—');
const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');

const Section = ({ no, title, children, testid }) => (
  <section className="dev-print-section" data-testid={testid}>
    <h2 className="mb-2 border-b-2 border-zinc-800 pb-1 text-base font-bold text-zinc-900">
      {no}. {title}
    </h2>
    {children}
  </section>
);

const Row = ({ label, value, testid }) => (
  <div className="flex gap-3 border-b border-zinc-200 py-1.5" data-testid={testid}>
    <dt className="w-40 shrink-0 text-xs font-semibold text-zinc-600">{label}</dt>
    <dd className="min-w-0 flex-1 whitespace-pre-wrap text-sm text-zinc-900">{value || '—'}</dd>
  </div>
);

/**
 * 開発案件出力 — 印刷用HTML。ブラウザの印刷機能で紙またはPDFに出力する。
 * 書類は一覧のみ（内容の複製はしない）。フェーズ2/3の項目はプレースホルダーで枠を確保する。
 */
export default function DevPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    devApi.print(id).then(setData).catch(() => toast.error('出力データを読み込めませんでした'));
  }, [id]);

  if (!data) return <div className="p-6 text-sm text-zinc-600" data-testid="dev-print-loading">読み込み中…</div>;

  const s = data.summary;
  const ps = data.procurement_summary || {};
  const prs = data.prototype_summary || {};
  const allValidations = (data.prototypes || []).flatMap((p) => p.validations || []);
  const requiredSlots = data.checklist.filter((c) => c.required);
  const optionalSlots = data.checklist.filter((c) => !c.required);
  const allDocs = [
    ...data.checklist.flatMap((c) => (c.documents || []).map((d) => ({ ...d, slot: c.label }))),
    ...(data.unassigned_documents || []).map((d) => ({ ...d, slot: '区分なし' })),
  ];

  return (
    <div data-testid="dev-print-page">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #dev-print-area, #dev-print-area * { visibility: visible; }
          #dev-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .dev-print-section { break-inside: avoid; }
          @page { size: A4; margin: 14mm; }
        }
      `}</style>

      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <Button variant="outline" onClick={() => nav(`/engineering/developments/${id}`)}
          data-testid="dev-print-back-btn">
          <ArrowLeft size={15} className="mr-1" /> 案件に戻る
        </Button>
        <Button onClick={() => window.print()} data-testid="dev-print-print-btn">
          <Printer size={15} className="mr-1" /> 印刷 / PDFに保存
        </Button>
        <p className="text-xs text-zinc-600">ブラウザの印刷画面で「PDFに保存」を選ぶとPDFになります。</p>
      </div>

      <div id="dev-print-area" className="mx-auto max-w-3xl space-y-6 bg-white p-6 text-zinc-900">
        <header className="border-b-4 border-zinc-900 pb-3">
          <p className="text-xs font-semibold text-zinc-600">開発案件出力</p>
          <h1 className="text-2xl font-bold" data-testid="dev-print-title">{s.title}</h1>
          <p className="mt-1 text-xs text-zinc-600">
            出力日時: {fmt(data.generated_at)} · 出力者: {data.generated_by}
          </p>
        </header>

        <Section no={1} title="案件サマリー" testid="dev-print-summary">
          <dl>
            <Row label="案件名" value={s.title} />
            <Row label="開発の種類" value={s.development_type_label} />
            <Row label="担当者" value={(s.owner_names || []).join('、')} />
            <Row label="現在のステージ" value={s.current_stage_label} testid="dev-print-stage" />
            <Row label="プロジェクト" value={s.project_name} />
            <Row label="導入先部門" value={(s.target_department_names || []).join('、')} />
            {s.intended_users && <Row label="想定利用者" value={s.intended_users} />}
            {s.install_location && <Row label="設置場所" value={s.install_location} />}
            <Row label="目標完了日" value={fmtDate(s.target_completion_date)} />
            <Row label="製作台数" value={data.build_quantity ? `${data.build_quantity}台` : '未入力'}
              testid="dev-print-build-qty" />
            <Row label="試作の準備" value={data.readiness?.message} testid="dev-print-readiness" />
            <Row label="作成" value={`${fmtDate(s.created_at)} · ${s.created_by_name || '—'}`} />
            {s.completed_at && <Row label="完了日" value={fmtDate(s.completed_at)} />}
          </dl>
        </Section>

        <Section no={2} title="構想提案" testid="dev-print-concept">
          <dl>
            {data.concept.filter((c) => c.value).map((c) => (
              <Row key={c.key} label={c.label} value={c.value} testid={`dev-print-concept-${c.key}`} />
            ))}
            {data.concept.every((c) => !c.value) && (
              <p className="py-2 text-sm text-zinc-500">構想提案は未入力です。</p>
            )}
          </dl>
        </Section>

        <Section no={3} title="書類チェックリスト" testid="dev-print-checklist">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-bold text-zinc-700">必須書類</p>
              {requiredSlots.map((c) => (
                <p key={c.key} className="border-b border-zinc-200 py-1 text-sm">
                  {c.label} — <span className={c.state === 'unregistered' ? 'font-semibold text-rose-700' : 'text-zinc-700'}>
                    {DOC_STATE_LABEL[c.state] || c.state}</span>
                </p>
              ))}
            </div>
            <div>
              <p className="mb-1 text-xs font-bold text-zinc-700">任意書類</p>
              {optionalSlots.map((c) => (
                <p key={c.key} className="border-b border-zinc-200 py-1 text-sm">
                  {c.label} — <span className="text-zinc-700">{DOC_STATE_LABEL[c.state] || c.state}</span>
                  {c.documents.length > 0 && ` (${c.documents.length}件)`}
                </p>
              ))}
            </div>
          </div>
        </Section>

        <Section no={4} title="必要部品" testid="dev-print-parts">
          {(data.required_parts || []).length === 0 ? (
            <p className="py-2 text-sm text-zinc-500">必要部品は登録されていません。</p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">部品</th>
                  <th className="py-1 pr-2">区分</th>
                  <th className="py-1 pr-2">図番 / 版</th>
                  <th className="py-1 pr-2 text-right">1台</th>
                  <th className="py-1 pr-2 text-right">必要</th>
                  <th className="py-1 pr-2 text-right">在庫</th>
                  <th className="py-1 pr-2 text-right">依頼済</th>
                  <th className="py-1 pr-2 text-right">不足</th>
                  <th className="py-1">状態</th>
                </tr>
              </thead>
              <tbody>
                {data.required_parts.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-200" data-testid={`dev-print-part-${p.id}`}>
                    <td className="py-1 pr-2">
                      {p.name}
                      {p.model_no ? <span className="text-xs text-zinc-600"> · {p.model_no}</span> : null}
                    </td>
                    <td className="py-1 pr-2 text-xs">{p.source_label}</td>
                    <td className="py-1 pr-2 text-xs">
                      {p.drawing_no || '—'}{p.drawing_revision ? ` ${p.drawing_revision}` : ''}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{p.qty_per_unit}</td>
                    <td className="py-1 pr-2 text-right font-semibold tabular-nums">{p.required_total}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">
                      {p.available_known ? p.available : '—'}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{p.reserved}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{p.need}</td>
                    <td className="py-1 text-xs">
                      {p.status_label}
                      {(p.missing_reasons || []).length > 0 ? `（${p.missing_reasons.join('・')}）` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section no={5} title="調達サマリー" testid="dev-print-procurement">
          <dl>
            <Row label="部品点数" value={`${ps.lines ?? 0}件`} />
            <Row label="在庫あり / 手配が必要 / 情報不足"
              value={`${ps.available ?? 0} / ${ps.need_procurement ?? 0} / ${ps.missing_info ?? 0}`}
              testid="dev-print-procurement-counts" />
            <Row label="依頼済みの部品" value={`${ps.requested_lines ?? 0}件`} />
            <Row label="不足合計" value={`${ps.total_need_qty ?? 0}`} />
            <Row label="判定" value={data.readiness?.message} />
          </dl>
          {(data.required_parts || []).filter((p) => p.need > 0).length > 0 && (
            <table className="mt-2 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">不足している部品</th>
                  <th className="py-1 pr-2 text-right">不足</th>
                  <th className="py-1 pr-2 text-right">入数</th>
                  <th className="py-1 pr-2 text-right">推奨発注</th>
                  <th className="py-1 pr-2 text-right">残る見込み</th>
                  <th className="py-1">依頼</th>
                </tr>
              </thead>
              <tbody>
                {data.required_parts.filter((p) => p.need > 0).map((p) => (
                  <tr key={p.id} className="border-b border-zinc-200">
                    <td className="py-1 pr-2">{p.name}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{p.need}{p.uom}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{p.pack_size || '—'}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">
                      {p.recommended_order_qty}
                      {p.recommended_packs ? `（${p.recommended_packs}パック）` : ''}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">{p.remaining_after_order}</td>
                    <td className="py-1 text-xs">
                      {(p.requests || []).map((r) => r.request_no).filter(Boolean).join('、') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section no={6} title="試作概要" testid="dev-print-prototypes">
          {(data.prototypes || []).length === 0 ? (
            <p className="py-2 text-sm text-zinc-500">試作は登録されていません。</p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">試作</th>
                  <th className="py-1 pr-2">製作日</th>
                  <th className="py-1 pr-2">台数</th>
                  <th className="py-1 pr-2">図面</th>
                  <th className="py-1 pr-2">投入部品</th>
                  <th className="py-1 pr-2">返却 / 手直し / スクラップ</th>
                  <th className="py-1">状態</th>
                </tr>
              </thead>
              <tbody>
                {data.prototypes.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-200" data-testid={`dev-print-proto-${p.id}`}>
                    <td className="py-1 pr-2">{p.name}</td>
                    <td className="py-1 pr-2 text-xs">{fmtDate(p.build_date)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{p.build_quantity}</td>
                    <td className="py-1 pr-2 text-xs">{p.drawing_revision_note || '—'}</td>
                    <td className="py-1 pr-2 text-xs">
                      {(p.installed_parts || []).map((r) => (
                        <span key={r.id} className="mr-2">
                          {r.name} {r.qty}{r.uom}
                          {r.drawing_revision ? `（${r.drawing_revision}）` : ''}
                        </span>
                      ))}
                      {(p.installed_parts || []).length === 0 ? '—' : ''}
                    </td>
                    <td className="py-1 pr-2 text-xs tabular-nums">
                      {p.part_counts.returned} / {p.part_counts.rework} / {p.part_counts.scrapped}
                    </td>
                    <td className="py-1 text-xs">{p.status_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section no={7} title="検証状況" testid="dev-print-validations">
          {allValidations.length === 0 ? (
            <p className="py-2 text-sm text-zinc-500">検証記録はありません。</p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">実施日</th>
                  <th className="py-1 pr-2">試作</th>
                  <th className="py-1 pr-2">検証した内容</th>
                  <th className="py-1 pr-2">判定</th>
                  <th className="py-1 pr-2">分類 / 発生箇所</th>
                  <th className="py-1 pr-2">対象部品 / 版</th>
                  <th className="py-1">不具合 / 原因 / 対策</th>
                </tr>
              </thead>
              <tbody>
                {allValidations.map((v) => (
                  <tr key={v.id} className="border-b border-zinc-200">
                    <td className="py-1 pr-2 text-xs">{fmtDate(v.tested_at)}</td>
                    <td className="py-1 pr-2 text-xs">{v.prototype_name}</td>
                    <td className="py-1 pr-2">{v.tested_item}</td>
                    <td className="py-1 pr-2 text-xs font-semibold">{v.result_label}</td>
                    <td className="py-1 pr-2 text-xs">
                      {v.failure_category_label || '—'}
                      {v.failure_location ? ` / ${v.failure_location}` : ''}
                      {v.recurrence ? '（再発）' : ''}
                    </td>
                    <td className="py-1 pr-2 text-xs">
                      {v.part_name || '—'}{v.drawing_revision ? ` / ${v.drawing_revision}` : ''}
                    </td>
                    <td className="py-1 text-xs">
                      {[v.issue_description, v.cause ? `原因: ${v.cause}` : null,
                        v.countermeasure ? `対策: ${v.countermeasure}` : null]
                        .filter(Boolean).join(' / ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <dl className="mt-2">
            <Row label="試作 / 進行中 / 合格 / 不合格"
              value={`${prs.prototypes ?? 0} / ${prs.open ?? 0} / ${prs.passed ?? 0} / ${prs.failed ?? 0}`}
              testid="dev-print-validation-counts" />
            <Row label="不具合記録 / 再発"
              value={`${prs.failure_records ?? 0} / ${prs.recurrences ?? 0}`}
              testid="dev-print-failure-counts" />
            <Row label="不具合の傾向"
              value={(data.failures || []).map((f) => `${f.label} ${f.count}件`).join('、') || '—'}
              testid="dev-print-failure-mix" />
            <Row label="返却 / 手直し / スクラップ（数量）"
              value={`${prs.returned_qty ?? 0} / ${prs.rework_qty ?? 0} / ${prs.scrapped_qty ?? 0}`} />
          </dl>
        </Section>

        <Section no={8} title="関連書類（一覧のみ）" testid="dev-print-documents">
          {allDocs.length === 0 && (data.external_refs || []).length === 0 ? (
            <p className="py-2 text-sm text-zinc-500">リンクされた書類はありません。</p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">区分</th>
                  <th className="py-1 pr-2">書類</th>
                  <th className="py-1 pr-2">番号 / 版</th>
                  <th className="py-1">更新日</th>
                </tr>
              </thead>
              <tbody>
                {allDocs.map((d) => (
                  <tr key={d.link_id} className="border-b border-zinc-200">
                    <td className="py-1 pr-2 text-xs text-zinc-600">{d.slot}</td>
                    <td className="py-1 pr-2">{d.title}</td>
                    <td className="py-1 pr-2 text-xs">
                      {d.document_number || '—'}{d.revision ? ` / ${d.revision}` : ''}
                    </td>
                    <td className="py-1 text-xs">{fmtDate(d.updated_at)}</td>
                  </tr>
                ))}
                {(data.external_refs || []).map((r) => (
                  <tr key={r.id} className="border-b border-zinc-200">
                    <td className="py-1 pr-2 text-xs text-zinc-600">共有フォルダー</td>
                    <td className="py-1 pr-2">{r.display_name}</td>
                    <td className="py-1 pr-2 text-xs">{r.revision_note || '—'}</td>
                    <td className="py-1 text-xs">{fmtDate(r.added_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section no={9} title="図面リビジョン履歴" testid="dev-print-revisions">
          {(data.revisions || []).length > 0 && (
            <table className="mb-2 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">部品 / 図番</th>
                  <th className="py-1 pr-2">版</th>
                  <th className="py-1">使用した試作と結果</th>
                </tr>
              </thead>
              <tbody>
                {data.revisions.flatMap((r) => r.versions.map((v, i) => (
                  <tr key={`${r.part_id}-${i}`} className="border-b border-zinc-200">
                    <td className="py-1 pr-2 text-xs">
                      {i === 0 ? `${r.part_name}${r.drawing_no ? ` / ${r.drawing_no}` : ''}` : ''}
                    </td>
                    <td className="py-1 pr-2 text-xs font-semibold">
                      {v.revision || '版なし'}{v.current ? '（現在）' : ''}
                    </td>
                    <td className="py-1 text-xs">
                      {v.used_in.length === 0 ? '—'
                        : v.used_in.map((u) => `${u.name}: ${u.result_label || u.status_label}`).join('、')}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          )}
          {(data.revision_history || []).length === 0 ? (
            <p className="py-2 text-sm text-zinc-500">図面リビジョンの変更履歴はありません。</p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">日時</th>
                  <th className="py-1 pr-2">部品</th>
                  <th className="py-1 pr-2">旧版</th>
                  <th className="py-1 pr-2">新版</th>
                  <th className="py-1">変更者 / 理由</th>
                </tr>
              </thead>
              <tbody>
                {data.revision_history.map((h, i) => (
                  <tr key={i} className="border-b border-zinc-200">
                    <td className="py-1 pr-2 text-xs">{fmt(h.changed_at)}</td>
                    <td className="py-1 pr-2">{h.part_name}</td>
                    <td className="py-1 pr-2 text-xs">
                      {h.drawing_no || '—'}{h.drawing_revision ? ` ${h.drawing_revision}` : ''}
                    </td>
                    <td className="py-1 pr-2 text-xs">
                      {h.replaced_by_drawing_no || '—'}
                      {h.replaced_by_revision ? ` ${h.replaced_by_revision}` : ''}
                    </td>
                    <td className="py-1 text-xs">
                      {h.changed_by_name}{h.note ? ` · ${h.note}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section no={10} title="ステージ履歴" testid="dev-print-history">
          {(data.stage_logs || []).length === 0 ? (
            <p className="py-2 text-sm text-zinc-500">履歴はまだありません。</p>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-400 text-left text-xs text-zinc-600">
                  <th className="py-1 pr-2">日時</th>
                  <th className="py-1 pr-2">ステージ</th>
                  <th className="py-1 pr-2">実施者</th>
                  <th className="py-1">備考</th>
                </tr>
              </thead>
              <tbody>
                {data.stage_logs.map((l) => (
                  <tr key={l.id} className="border-b border-zinc-200">
                    <td className="py-1 pr-2 text-xs">{fmt(l.performed_at)}</td>
                    <td className="py-1 pr-2">
                      {l.from_stage_label ? `${l.from_stage_label} → ` : ''}{l.to_stage_label}
                      {l.forced && <span className="ml-1 text-xs font-semibold text-amber-700">（例外進行）</span>}
                    </td>
                    <td className="py-1 pr-2 text-xs">{l.performed_by_name}</td>
                    <td className="py-1 text-xs">{l.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </div>
  );
}
